/**
 * FrequencySelector — Composant réutilisable pour la sélection de fréquences
 * =========================================================================
 * 
 * Ce composant DOIT être utilisé dans TOUS les modules nécessitant une sélection
 * de fréquence pour garantir une cohérence totale de l'application.
 * 
 * Version: 1.0.0
 * Date: Avril 2026
 * 
 * MODULES UTILISANT CE COMPOSANT:
 * - RecurringOperationForm (revenus/dépenses)
 * - CreateSavingsGoalWizard (objectifs)
 * - CreatePortefeuilleWizard (portefeuilles)
 * - FinancialSetupWizard (onboarding)
 * - FinancialFoundation
 * - OperationForm
 */

import React from 'react';
import { motion } from 'framer-motion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { AlertCircle } from 'lucide-react';
import { 
  FREQUENCIES, 
  CUSTOM_FREQUENCY_UNITS, 
  getFrequencyByValue,
  requiresDayOfMonth 
} from '../../constants/frequencies';

/**
 * FrequencySelector - Sélecteur de fréquence harmonisé
 * 
 * @param {string} value - Valeur actuelle de la fréquence (ex: 'mensuel', 'hebdomadaire')
 * @param {function} onChange - Callback appelé lors du changement (frequencyType, frequencyUnit, frequencyValue)
 * @param {string} frequencyUnit - Unité pour fréquence personnalisée ('jours', 'semaines', 'mois', 'annees')
 * @param {number} frequencyValue - Valeur pour fréquence personnalisée
 * @param {string} variant - 'full' (avec boutons rapides) ou 'compact' (dropdown seul)
 * @param {array} allowedFrequencies - Liste des fréquences autorisées (null = toutes)
 * @param {string} className - Classes CSS additionnelles
 * @param {boolean} showDescription - Afficher la description sous les options
 */
const FrequencySelector = ({
  value = 'mensuel',
  onChange,
  frequencyUnit = 'mois',
  frequencyValue = 1,
  variant = 'full',
  allowedFrequencies = null,
  className = '',
  showDescription = true,
  colorScheme = 'purple' // 'purple', 'teal', 'blue', 'emerald'
}) => {
  
  // Filtrer les fréquences si une liste est fournie
  const availableFrequencies = allowedFrequencies 
    ? FREQUENCIES.filter(f => allowedFrequencies.includes(f.value))
    : FREQUENCIES;
  
  // Fréquences rapides (boutons)
  const quickFrequencies = availableFrequencies.filter(f => 
    ['hebdomadaire', 'quinzaine', 'mensuel'].includes(f.value)
  );
  
  // Autres fréquences (dropdown)
  const otherFrequencies = availableFrequencies.filter(f => 
    !['hebdomadaire', 'quinzaine', 'mensuel'].includes(f.value)
  );
  
  // Gestion du changement de fréquence
  const handleFrequencyChange = (newValue) => {
    const selected = getFrequencyByValue(newValue);
    if (selected) {
      let newUnit = frequencyUnit;
      let newFreqValue = frequencyValue;
      
      if (newValue === 'personnalise') {
        newUnit = 'jours';
        newFreqValue = 1;
      } else if (selected.unit && selected.unitValue) {
        newUnit = selected.unit;
        newFreqValue = selected.unitValue;
      }
      
      onChange(newValue, newUnit, newFreqValue);
    }
  };
  
  // Couleurs selon le schéma
  const colors = {
    purple: {
      bg: 'bg-purple-50',
      border: 'border-purple-200',
      text: 'text-purple-800',
      textLight: 'text-purple-600',
      active: 'bg-purple-600 text-white',
      hover: 'hover:border-purple-400',
      ring: 'focus:ring-purple-400'
    },
    teal: {
      bg: 'bg-teal-50',
      border: 'border-teal-200',
      text: 'text-teal-800',
      textLight: 'text-teal-600',
      active: 'bg-teal-600 text-white',
      hover: 'hover:border-teal-400',
      ring: 'focus:ring-teal-400'
    },
    blue: {
      bg: 'bg-blue-50',
      border: 'border-blue-200',
      text: 'text-blue-800',
      textLight: 'text-blue-600',
      active: 'bg-blue-600 text-white',
      hover: 'hover:border-blue-400',
      ring: 'focus:ring-blue-400'
    },
    emerald: {
      bg: 'bg-emerald-50',
      border: 'border-emerald-200',
      text: 'text-emerald-800',
      textLight: 'text-emerald-600',
      active: 'bg-emerald-600 text-white',
      hover: 'hover:border-emerald-400',
      ring: 'focus:ring-emerald-400'
    }
  };
  
  const c = colors[colorScheme] || colors.purple;
  const isQuickSelected = quickFrequencies.some(f => f.value === value);
  
  return (
    <div className={`${c.bg} rounded-xl p-4 space-y-3 ${className}`} data-testid="frequency-selector">
      <Label className={`${c.text} font-semibold`}>Fréquence</Label>
      
      {/* Variant full : boutons rapides + dropdown */}
      {variant === 'full' && quickFrequencies.length > 0 && (
        <>
          {/* Fréquences rapides (boutons) */}
          <div className="grid grid-cols-3 gap-2">
            {quickFrequencies.map(freq => (
              <button
                key={freq.value}
                type="button"
                onClick={() => handleFrequencyChange(freq.value)}
                className={`p-2 rounded-lg text-sm font-medium transition-all ${
                  value === freq.value
                    ? c.active
                    : `bg-white text-gray-700 border ${c.border} ${c.hover}`
                }`}
                data-testid={`freq-btn-${freq.value}`}
              >
                {freq.shortLabel}
              </button>
            ))}
          </div>
          
          {/* Autres fréquences (dropdown) */}
          {otherFrequencies.length > 0 && (
            <div className={`pt-2 border-t ${c.border}`}>
              <Label className={`text-xs ${c.textLight} mb-2 block`}>Autres fréquences</Label>
              <Select
                value={otherFrequencies.some(f => f.value === value) ? value : ''}
                onValueChange={handleFrequencyChange}
              >
                <SelectTrigger 
                  className={`w-full ${
                    otherFrequencies.some(f => f.value === value)
                      ? `${c.active} border-transparent`
                      : 'bg-white'
                  }`}
                  data-testid="freq-dropdown"
                >
                  <SelectValue placeholder="Choisir une autre fréquence..." />
                </SelectTrigger>
                <SelectContent>
                  {otherFrequencies.map(freq => (
                    <SelectItem key={freq.value} value={freq.value}>
                      <div className="flex flex-col">
                        <span className="font-medium">{freq.label}</span>
                        {showDescription && (
                          <span className="text-xs text-gray-500">{freq.description}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </>
      )}
      
      {/* Variant compact : dropdown seul */}
      {variant === 'compact' && (
        <Select value={value} onValueChange={handleFrequencyChange}>
          <SelectTrigger className="w-full bg-white" data-testid="freq-dropdown-compact">
            <SelectValue placeholder="Choisir une fréquence..." />
          </SelectTrigger>
          <SelectContent>
            {availableFrequencies.map(freq => (
              <SelectItem key={freq.value} value={freq.value}>
                <div className="flex flex-col">
                  <span className="font-medium">{freq.label}</span>
                  {showDescription && (
                    <span className="text-xs text-gray-500">{freq.description}</span>
                  )}
                </div>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}
      
      {/* Options personnalisées */}
      {value === 'personnalise' && (
        <motion.div 
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className={`bg-white rounded-lg p-3 mt-2 border ${c.border}`}
        >
          <Label className="text-sm text-gray-600 mb-2 block">Définir la fréquence</Label>
          <div className="flex items-center gap-2">
            <span className="text-sm text-gray-600">Tous les</span>
            <Input
              type="number"
              min="1"
              max="365"
              value={frequencyValue}
              onChange={(e) => onChange(value, frequencyUnit, parseInt(e.target.value) || 1)}
              className={`w-16 text-center ${c.ring}`}
              data-testid="custom-freq-value"
            />
            <Select
              value={frequencyUnit}
              onValueChange={(unit) => onChange(value, unit, frequencyValue)}
            >
              <SelectTrigger className="w-32" data-testid="custom-freq-unit">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CUSTOM_FREQUENCY_UNITS.map(unit => (
                  <SelectItem key={unit.value} value={unit.value}>
                    {unit.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </motion.div>
      )}
      
      {/* Message pour fréquence unique */}
      {value === 'unique' && (
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="bg-amber-50 rounded-lg p-3 border border-amber-200"
        >
          <p className="text-sm text-amber-800">
            <AlertCircle className="inline h-4 w-4 mr-1" />
            Cette opération n'apparaîtra qu'une seule fois, à la date de début.
          </p>
        </motion.div>
      )}
    </div>
  );
};

export default FrequencySelector;

// Export des helpers pour les modules qui en ont besoin directement
export { requiresDayOfMonth };
